import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { EDeviceThermoModel } from 'src/app/conf/device-models';
import { Device } from 'src/app/model/device';
import { Thermo } from 'src/app/model/thermo';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService } from 'primeng/dynamicdialog';
import { ThermoExportLogDialogComponent } from '../thermo-export-log-dialog/thermo-export-log.dialog.component';
import * as DeviceThActions from 'src/app/store/device-th/device-th.actions';
import { selectCurrentDeviceThermo } from 'src/app/store/device-th/device-th.selector';
import { selectCurrentDevice } from 'src/app/store/device/device.selector';
import { selectIsImpersMode } from 'src/app/store/user/user.selector';

@Component({
	selector: 'app-thermo-commands',
	templateUrl: './thermo-commands.component.html',
	providers: [DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})

export class ThermoCommandsComponent implements OnInit, OnDestroy {
	thermoDevicesSub: Subscription;
	deviceSub: Subscription;

	thermo: Thermo;
	device: Device = new Device();
	thermoForm: FormGroup;

	spinner: number;
	progressUpdate: boolean;
	thermoUpdate: boolean;
	tmUpdateThermo: number;

	termalControlOptions: any[] = [];
	seasonControlOptions: any[] = [];

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	screenWidth: any;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	ngOnInit(): void {
		this.deviceSub = this.store.select(selectCurrentDevice).subscribe(
			device => {
				this.device = device;
			}
		);
		this.thermoDevicesSub = this.store.select(selectCurrentDeviceThermo).subscribe(
			deviceThermo => {
				this.thermo = deviceThermo;
				if (this.thermo !== undefined) {
					this.createFormGroup();
					this.progressUpdate = false;
				} else {
					this.thermo = null;
				}
			}
		);
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
				if (this.device) {
					if (this.isImpersMode) {
						this.store.dispatch(DeviceThActions.openWebsocketThermo({ userId: this.device.OwnerId }));
						this.store.dispatch(DeviceThActions.getThermoDeviceV2({ keycode: this.device.Keycode, userId: this.device.OwnerId }));
					} else {
						this.store.dispatch(DeviceThActions.openWebsocketThermo({}));
						this.store.dispatch(DeviceThActions.getThermoDeviceV2({ keycode: this.device.Keycode }));
					}
				}
			}
		);
		this.thermoUpdate = false;
		this.progressUpdate = false;
		this.onResize();
		this.translate.onLangChange.subscribe(() => {
			this.contolCommandTh();
		});
	}

	ngOnDestroy(): void {
		this.thermoDevicesSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.store.dispatch(DeviceThActions.closeWebsocketThermo());
	}

	createFormGroup(): void {
		this.spinner = this.thermo.manual_temp;
		if (this.thermo.mode === 'null' || this.thermo.mode === 'temp_off') {
			this.thermo.mode = 'off';
		}
		this.thermoForm = new FormGroup({
			'thermalControl': new FormControl(this.thermo.mode),
			'seasonControl': new FormControl(this.thermo.current_season),
			'temperature': new FormControl(this.thermo.manual_temp),
		});
		this.contolCommandTh();
	}

	contolCommandTh(): void {
		this.termalControlOptions = [
			{
				icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
			{
				icon: 'pi cm-handle-set', name: this.translate.instant('TH_MANUAL'), value: 'manual',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
			{
				icon: 'pi cm-time', name: this.translate.instant('TH_AUTO'), value: 'auto',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
		];
		this.seasonControlOptions = [
			{ icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off' },
			{ icon: 'pi cm-sun', name: this.translate.instant('TH_SUMMER'), value: 'summer' },
			{ icon: 'pi cm-snow', name: this.translate.instant('TH_WINTER'), value: 'winter' },
		];
		this.insertControlHoliday();
		this.insertControlBoost();
		this.insertControlJolly();
		this.insertControlTempMan();
		this.insertControlAntifreeze();
	}

	insertControlHoliday(): void {
		if (this.isVisibleControlHoliday()) {
			this.termalControlOptions.push({
				icon: 'pi cm-holiday', name: this.translate.instant('TH_HOLIDAY'), value: 'holiday',
				disabled: true
			});
		}
	}

	insertControlBoost(): void {
		if (this.isVisibleControlBoost()) {
			this.termalControlOptions.push({
				icon: 'pi cm-setting-bolt', name: this.translate.instant('TH_BOOST'), value: 'boost',
				disabled: true
			},);
		}
	}

	insertControlJolly(): void {
		if (this.isVisibleControlJolly()) {
			this.termalControlOptions.push({
				icon: 'pi cm-jolly', name: this.translate.instant('TH_JOLLY'), value: 'jolly',
				disabled: true
			},);
		}
	}

	insertControlTempMan(): void {
		if (this.isVisibleControlTempMan()) {
			this.termalControlOptions.push({
				icon: 'pi cm-handle-set', name: this.translate.instant('TH_TEMP_MAN'), value: 'temp_man',
				disabled: true
			});
		}
	}

	insertControlAntifreeze(): void {
		if (this.isVisibleControlAntifreeze()) {
			this.seasonControlOptions.push({ icon: 'pi cm-antifreeze', name: this.translate.instant('TH_ANTIFREEZE'), value: 'antifreeze' });
		}
	}

	thermalControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate.keycode = this.thermo.keycode;
		//thermoUpdate.devcode = this.thermo.devcode;
		thermoUpdate.set_mode = this.f.thermalControl.value;
		if (!this.isImpersMode) {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate }));
		} else {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate, userId: this.device.OwnerId }));
		}
	}

	seasonControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate.keycode = this.thermo.keycode;
		//thermoUpdate.devcode = this.thermo.devcode;
		thermoUpdate.current_season = this.f.seasonControl.value;
		if (!this.isImpersMode) {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate }));
		} else {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate, userId: this.device.OwnerId }));
		}
	}

	isVisibleControlHoliday(): boolean {
		return this.device.ModelId === EDeviceThermoModel.TH700 ||
			this.device.ModelId === EDeviceThermoModel.TH700WALL ||
			this.device.ModelId === EDeviceThermoModel.TH750WALL ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT;
	}

	isVisibleControlBoost(): boolean {
		return this.device.ModelId === EDeviceThermoModel.TH700 ||
			this.device.ModelId === EDeviceThermoModel.TH700WALL ||
			this.device.ModelId === EDeviceThermoModel.TH750WALL ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT;
	}

	isVisibleControlJolly(): boolean {
		return this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550;
	}

	isVisibleControlTempMan(): boolean {
		return this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550 ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT;
	}

	isVisibleControlAntifreeze(): boolean {
		return this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550;
	}

	isVisibleManualControlTemperature(): boolean {
		return this.thermoForm.value.thermalControl === 'manual';
	}

	sliderChanged(args: any): void {
		this.spinner = Number((args).toFixed(1));
		this.updateThermo();
	}

	sliderStop(args: any): number {
		this.spinner = Number((args).toFixed(1));
		setTimeout(() => {
			this.executeUpdate(args);
		}, 2000);
		return this.spinner;
	}

	executeUpdate(value: number): void {
		if (this.spinner === Number((value).toFixed(1))) {
			this.updateThermo();
		}
	}

	updateThermo(): void {
		if (!this.progressUpdate) {
			this.progressUpdate = true;
			const thermoUpdate = new Thermo();
			thermoUpdate.keycode = this.thermo.keycode;
			//thermoUpdate.devcode = this.thermo.devcode;
			thermoUpdate.set_manual_temp = this.spinner;
			if (!this.isImpersMode) {
				this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate }));
			} else {
				this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate, userId: this.device.OwnerId }));
			}
			this.progressUpdate = false;
		}
	}

	setSliderDecreaseValue(): void {
		if (!this.thermoUpdate) {
			this.thermoUpdate = true;
			this.tmUpdateThermo = 2;
			this.runInterval();
		} else {
			this.tmUpdateThermo = 2;
		}
		const value = Number((this.spinner - 0.1).toFixed(1));
		this.f.temperature.setValue(value);
		this.spinner = value;
	}

	setSliderIncreaseValue(): void {
		const value = Number((this.spinner + 0.1).toFixed(1));
		this.f.temperature.setValue(value);
		this.spinner = value;
		if (!this.thermoUpdate) {
			this.thermoUpdate = true;
			this.tmUpdateThermo = 2;
			this.runInterval();
		} else {
			this.tmUpdateThermo = 2;
		}
	}

	isSliderMin(): boolean {
		return this.f.temperature.value <= 5;
	}

	isSliderMax(): boolean {
		return this.f.temperature.value >= 35;
	}

	runInterval(): void {
		const interval = setInterval(() => {
			this.tmUpdateThermo--;
			if (this.tmUpdateThermo <= 0) {
				clearInterval(interval);
				this.thermoUpdate = false;
				this.updateThermo();
			}
		}, 500);
	}

	getFlex(): string {
		if (this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550) {
			return '25%';
		}
		return '33%';
	}

	exportLog(): void {
		this.store.dispatch(DeviceThActions.getLogThExportReset());
		this.dialogService.open(ThermoExportLogDialogComponent, {
			header: this.translate.instant('EXPORT_METRICS_LIST'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	get f(): any {
		return this.thermoForm.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}
}
